import { computed, readonly, ref, watch } from 'vue';
import { useConfigStore } from '~/stores/config';
import { useConfigParserStore } from '~/stores/configParser';
import { getDifferenceToConfigFileStructure, filterConfigValues, getCategoryXPaths, countElementsInModules, groupDifferencesByModule } from '~/Common/ConfigHelpers';
import { createFilteredModules } from '~/Common/ConfigFactories';
import type { ConfigModule } from '~/types/config';
import type { DeleteCategories } from '~/Common/DeleteCategories';
import { useToast } from 'primevue/usetoast';
import { useI18n } from 'vue-i18n';


/**
 * Creates a cache key for the given config values and structure timestamp
 * @param configValues - The configuration values
 * @param timestamp - The structure timestamp
 * @return A string cache key
 */
function createCacheKey(configValues: Record<string, string>, timestamp: number): string {
  const valuesHash = Object.keys(configValues)
    .sort()
    .map(key => `${key}:${configValues[key]}`)
    .join('|');
  return `${timestamp}-${valuesHash}`;
}

/**
 * Checks if the cache is valid for the given cache key
 * @param currentCacheKey - The current cache key to check
 * @param lastCacheKey - The last cached key
 * @param cachedChanges - The cached changes
 * @return True if cache is valid, false otherwise
 */
function isCacheValid(
  currentCacheKey: string,
  lastCacheKey: string,
  cachedChanges: ConfigModule[]
): boolean {
  return currentCacheKey === lastCacheKey && cachedChanges.length >= 0;
}

/**
 * Creates a cache key including deleted categories information
 * @param configValues - The configuration values
 * @param structureTimestamp - The structure timestamp
 * @param deleteCategories - Optional delete categories instance
 * @return A complete cache key string
 */
function createCompleteCacheKey(
  configValues: Record<string, string>,
  structureTimestamp: number,
  deleteCategories?: DeleteCategories
): string {
  const deletedCategoriesKey = deleteCategories ?
    JSON.stringify(getCategoryXPaths(deleteCategories.deletedCategories)) : '';
  return createCacheKey(configValues, structureTimestamp) + deletedCategoriesKey;
}

/**
 * Composable function to track configuration changes
 * @param deleteCategories - Optional instance of DeleteCategories to track deleted categories
 */
export function useConfigChanges(
  deleteCategories?: DeleteCategories
) {
  const configStore = useConfigStore();
  const configParserStore = useConfigParserStore();
  const toast = useToast();
  const { t } = useI18n();

  // Cache for expensive computations
  const lastCacheKey = ref<string>('');
  const cachedChanges = ref<ConfigModule[]>([]);
  const structureTimestamp = ref<number>(Date.now());

  const changes = computed<ConfigModule[]>(() => {
    if (!configStore.configFileStructure || !configParserStore.getConfigValues) {
      return [];
    }

    // Make configValues computed for reactivity
    const configValues = configParserStore.getConfigValues as Record<string, string>;

    // Create complete cache key
    const currentCacheKey = createCompleteCacheKey(
      configValues,
      structureTimestamp.value,
      deleteCategories
    );

    // Return cached result if available
    if (isCacheValid(currentCacheKey, lastCacheKey.value, cachedChanges.value)) {
      return cachedChanges.value;
    }

    // Compute changes
    const computedChanges = computeConfigChanges(configValues);

    // Cache the results
    cachedChanges.value = computedChanges;
    lastCacheKey.value = currentCacheKey;

    return computedChanges;
  });

  const hasChanges = computed(() => changes.value.length > 0);

  const changesCount = computed(() => {
    return countElementsInModules(changes.value);
  });

  // Watch for changes in organized config data to invalidate cache
  watch(() => configParserStore.getOrganizedConfigData, () => {
    structureTimestamp.value = Date.now();
    lastCacheKey.value = '';
  }, { deep: true });

  // Watch for changes in deleted categories to invalidate cache
  if (deleteCategories) {
    watch(() => deleteCategories.deletedModulesRef.value, () => {
      structureTimestamp.value = Date.now();
      lastCacheKey.value = '';
    }, { deep: true });
  }

  // Watch for changes in configValues to invalidate cache
  watch(() => configParserStore.getConfigValues, () => {
    structureTimestamp.value = Date.now();
    lastCacheKey.value = '';
  }, { deep: true });

  /**
   * Computes the configuration changes by comparing current values with original structure
   * @return Array of ConfigModule objects containing changes
   */
  function computeConfigChanges(configValues: Record<string, string>): ConfigModule[] {
    if (!configValues || !configStore.getElementLookupMap || !configParserStore.getOrganizedConfigData) {
      return [];
    }
    try {
      // Get values directly from stores and refs
      const currentElements = configStore.getElementLookupMap;
      const organizedData = configParserStore.getOrganizedConfigData as Readonly<Record<number, ConfigModule>>;

      // Filter config values using helper function
      const filteredConfigValues = filterConfigValues(
        configValues,
        [
          ...configParserStore.getHiddenCategories(),
          ...deleteCategories ? deleteCategories.deletedCategories : []
        ]
      );

      const differences = getDifferenceToConfigFileStructure(currentElements, filteredConfigValues);

      if (differences.length === 0) {
        return [];
      }

      // Group differences by module using the new helper function
      const differencesByModule = groupDifferencesByModule(differences, currentElements);

      // Create filtered modules containing only changed elements using the factory function
      return createFilteredModules(differencesByModule, organizedData);
    } catch (error) {
      console.error('Error computing configuration changes:', error);
      toast.add({
        severity: 'error',
        summary: t('common.toast.error'),
        detail: t('common.dialogs.changes.changeError'),
        life: 3000
      });
      return [];
    }
  }

  return {
    changes: readonly(changes),
    hasChanges: readonly(hasChanges),
    changesCount: readonly(changesCount)
  };
}
